import { useCallback, useEffect } from 'react'
import { useMachine, useSelector } from '@xstate/react'
import { emergencyMachine, type EmergencyStateId } from './machine'
import { EmergencyActorProvider } from './context'
import { useFlowBackHandler, useOptionalSelfService } from '../../context'
import { frontdeskDiagnosticLogger } from '../../../../lib/diagnostic-logger'
import { useFlowStepProgress } from '../use-flow-step-progress'
import { EmergencyViewStep } from './steps/EmergencyViewStep'

/** First state of the emergency flow - back from here exits the flow */
const FIRST_STATE: EmergencyStateId = 'emergency'

interface EmergencyFlowProps {
  onComplete?: () => void
}

interface EmergencyCurrentStepProps {
  currentState: EmergencyStateId
  onComplete?: () => void
}

/** Compile-time proof that every emergency state is rendered; never a debug string (FC-21). */
function assertNoUnrenderedEmergencyState(state: never): null {
  frontdeskDiagnosticLogger.error('[Emergency] no composition for machine state; rendering nothing', { state })
  return null
}

function EmergencyCurrentStep({ currentState, onComplete }: EmergencyCurrentStepProps) {
  switch (currentState) {
    case 'emergency':
      return <EmergencyViewStep onReturnToStart={onComplete} />
  }
  return assertNoUnrenderedEmergencyState(currentState)
}

/**
 * Emergency flow orchestrator component.
 * Uses XState to manage the emergency alert flow.
 *
 * Displays emergency alert with audio controls and navigation back to start.
 */
export function EmergencyFlow({ onComplete }: EmergencyFlowProps) {
  const [, send, actorRef] = useMachine(emergencyMachine)

  const currentState = useSelector(actorRef, (s) => s.value as EmergencyStateId)

  // Emergency hides the global footer stepper — no step pressure in a crisis.
  useFlowStepProgress(currentState, null)

  // Register back handler with parent - exit flow if at first state
  const handleBack = useCallback(() => {
    if (currentState === FIRST_STATE) {
      onComplete?.()
    } else {
      send({ type: 'BACK' })
    }
  }, [currentState, onComplete, send])

  useFlowBackHandler(handleBack)

  // One exit only: the alarm card's "Zurück zum Start" (KO-96).
  const setBackHidden = useOptionalSelfService()?.setBackHidden
  useEffect(function hideFooterBackDuringEmergency() {
    setBackHidden?.(true)
  }, [setBackHidden])

  return (
    <EmergencyActorProvider actor={actorRef}>
      <EmergencyCurrentStep currentState={currentState} onComplete={onComplete} />
    </EmergencyActorProvider>
  )
}
